"use client"

import { useEffect, useState } from "react"
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    Button,
    useDisclosure,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    IconButton,
    VStack,
    Grid,
    GridItem,
    FormControl,
    FormLabel,
    HStack,
    Tooltip,
    Text,
    useToast,
    Skeleton,
    SkeletonText,
    Switch,
} from "@chakra-ui/react"
import { useDispatch, useSelector } from "react-redux"
import {
    fetchAdminResumeBuildersAsync,
    selectAdminResumeBuilder,
    addAdminResumeBuilderAsync,
    updateAdminResumeBuilderAsync,
    deleteAdminResumeBuilderAsync,
} from "../../AdminResumeBuilder/AdminResumeBuilderSlice"
import { useForm } from "react-hook-form"
import { DeleteIcon, EditIcon } from "@chakra-ui/icons"

const AdminResumeBuilder = () => {
    const { register, handleSubmit, setValue, reset, watch } = useForm()
    const dispatch = useDispatch()
    const resumeBuilders = useSelector(selectAdminResumeBuilder)
    const toast = useToast()

    const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
    const [selectedBuilder, setSelectedBuilder] = useState(null)
    const [deletingBuilder, setDeletingBuilder] = useState(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const fetchData = async () => {
            try {
                await dispatch(fetchAdminResumeBuildersAsync())
                setIsLoading(false)
            } catch (error) {
                setIsLoading(false)
                toast({
                    title: "Error",
                    description: "Failed to fetch resume builders",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                })
            }
        }
        fetchData()
    }, [dispatch, toast])

    const handleOpenAdd = () => {
        reset()
        setValue("is_active", 1)
        onAddOpen()
    }

    const handleOpenEdit = (builder) => {
        setSelectedBuilder(builder)
        setValue("title", builder.title)
        setValue("resume_identity", builder.resume_identity)
        setValue("is_active", builder.is_active)
        onEditOpen()
    }

    const handleSave = async (data) => {
        try {
            await dispatch(addAdminResumeBuilderAsync(data)).unwrap()
            await dispatch(fetchAdminResumeBuildersAsync())
            onAddClose()
            reset()
            toast({
                title: "Success",
                description: "Resume builder added successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            })
        } catch (error) {
            console.error("Failed to add resume builder:", error)
            toast({
                title: "Error",
                description: "Failed to add resume builder",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            })
        }
    }

    const handleOpenDelete = (builder) => {
        setDeletingBuilder(builder)
        onDeleteOpen()
    }

    const handleUpdate = async (data) => {
        if (!selectedBuilder) return
        try {
            const updatedData = {
                uuid: selectedBuilder.uuid,
                resumeBuilderData: {
                    title: data.title,
                    resume_identity: data.resume_identity,
                    is_active: data.is_active ? 1 : 0,
                },
            }

            await dispatch(updateAdminResumeBuilderAsync(updatedData)).unwrap()
            await dispatch(fetchAdminResumeBuildersAsync())
            onEditClose()
            reset()
            setSelectedBuilder(null)
            toast({
                title: "Success",
                description: "Resume builder updated successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            })
        } catch (error) {
            console.error("Failed to update resume builder:", error)
            toast({
                title: "Error",
                description: "Failed to update resume builder",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            })
        }
    }

    const handleDelete = async () => {
        if (!deletingBuilder) return
        try {
            await dispatch(deleteAdminResumeBuilderAsync(deletingBuilder.uuid)).unwrap()
            await dispatch(fetchAdminResumeBuildersAsync())
            onDeleteClose()
            setDeletingBuilder(null)
            toast({
                title: "Success",
                description: "Resume builder deleted successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            })
        } catch (error) {
            console.error("Failed to delete resume builder:", error)
            toast({
                title: "Error",
                description: "Failed to delete resume builder",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            })
        }
    }

    const SkeletonTable = () => (
        <TableContainer>
            <Table size="sm">
                <Thead className="bg-gray-200">
                    <Tr>
                        <Th>ID</Th>
                        <Th>Title</Th>
                        <Th>Resume Identity</Th>
                        <Th>Status</Th>
                        <Th>Created At</Th>
                        <Th>Action</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {[...Array(5)].map((_, index) => (
                        <Tr key={index}>
                            <Td>
                                <Skeleton height="20px" width="30px" />
                            </Td>
                            <Td>
                                <SkeletonText noOfLines={1} spacing="4" skeletonHeight="2" />
                            </Td>
                            <Td>
                                <SkeletonText noOfLines={1} spacing="4" skeletonHeight="2" />
                            </Td>
                            <Td>
                                <Skeleton height="20px" width="60px" />
                            </Td>
                            <Td>
                                <SkeletonText noOfLines={1} spacing="4" skeletonHeight="2" />
                            </Td>
                            <Td>
                                <HStack spacing={2}>
                                    <Skeleton height="32px" width="32px" borderRadius="md" />
                                    <Skeleton height="32px" width="32px" borderRadius="md" />
                                </HStack>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
        </TableContainer>
    )

    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        })
    }

    return (
        <div className="h-screen bg-adminbg contentAdmin p-4">
            <div className="w-full my-5 bg-white rounded-md">
                <div className="flex justify-between">
                    <p className="text-xl text-black font-semibold px-3 py-7">Resume Builder List</p>
                    <div className="py-4 px-3 flex gap-3">
                        <Button colorScheme="blue" fontSize="medium" onClick={handleOpenAdd}>
                            Add <i className="fa-solid fa-plus px-2"></i>
                        </Button>
                    </div>
                </div>

                {/* Add Resume Builder Modal */}
                <Modal isOpen={isAddOpen} onClose={onAddClose} size="4xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                            Add New Resume Builder
                        </ModalHeader>
                        <ModalCloseButton color="white" />
                        <form onSubmit={handleSubmit(handleSave)}>
                            <ModalBody py={6}>
                                <VStack spacing={6}>
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                                        <GridItem colSpan={2}>
                                            <FormControl>
                                                <FormLabel>Title</FormLabel>
                                                <Input
                                                    placeholder="Enter resume builder title"
                                                    color="black"
                                                    {...register("title", { required: true })}
                                                />
                                            </FormControl>
                                        </GridItem>
                                        <GridItem colSpan={2}>
                                            <FormControl>
                                                <FormLabel>Resume Identity</FormLabel>
                                                <Input
                                                    placeholder="Enter resume identity (e.g., SoftwareDeveloper)"
                                                    color="black"
                                                    {...register("resume_identity", { required: true })}
                                                />
                                            </FormControl>
                                        </GridItem>
                                        <GridItem colSpan={2}>
                                            <FormControl display="flex" alignItems="center">
                                                <FormLabel mb="0">Active Status</FormLabel>
                                                <Switch {...register("is_active")} defaultChecked={true} colorScheme="green" />
                                            </FormControl>
                                        </GridItem>
                                    </Grid>
                                </VStack>
                            </ModalBody>
                            <ModalFooter bg="gray.50" borderBottomRadius="md">
                                <Button colorScheme="blue" mr={3} type="submit">
                                    Save
                                </Button>
                                <Button onClick={onAddClose}>Cancel</Button>
                            </ModalFooter>
                        </form>
                    </ModalContent>
                </Modal>

                {/* Edit Resume Builder Modal */}
                <Modal isOpen={isEditOpen} onClose={onEditClose} size="4xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                            Edit Resume Builder
                        </ModalHeader>
                        <ModalCloseButton color="white" />
                        <form onSubmit={handleSubmit(handleUpdate)}>
                            <ModalBody py={6}>
                                <VStack spacing={6}>
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                                        <GridItem colSpan={2}>
                                            <FormControl>
                                                <FormLabel>Title</FormLabel>
                                                <Input
                                                    placeholder="Enter resume builder title"
                                                    color="black"
                                                    {...register("title", { required: true })}
                                                />
                                            </FormControl>
                                        </GridItem>
                                        <GridItem colSpan={2}>
                                            <FormControl>
                                                <FormLabel>Resume Identity</FormLabel>
                                                <Input
                                                    placeholder="Enter resume identity"
                                                    color="black"
                                                    {...register("resume_identity", { required: true })}
                                                />
                                            </FormControl>
                                        </GridItem>
                                        <GridItem colSpan={2}>
                                            <FormControl display="flex" alignItems="center">
                                                <FormLabel mb="0">Active Status</FormLabel>
                                                <Switch {...register("is_active")} colorScheme="green" />
                                            </FormControl>
                                        </GridItem>
                                    </Grid>
                                </VStack>
                            </ModalBody>
                            <ModalFooter bg="gray.50" borderBottomRadius="md">
                                <Button colorScheme="blue" mr={3} type="submit">
                                    Update
                                </Button>
                                <Button onClick={onEditClose}>Cancel</Button>
                            </ModalFooter>
                        </form>
                    </ModalContent>
                </Modal>

                {/* Delete Modal */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <ModalOverlay />
                    <ModalContent maxW="2xl" borderradius="lg" boxShadow="lg">
                        <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                            Confirm Deletion
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Text fontSize="md" textAlign="center">
                                Are you sure you want to delete{" "}
                                <Text as="span" fontWeight="semibold" color="red.500">
                                    "{deletingBuilder?.title}"
                                </Text>
                                ?
                            </Text>
                        </ModalBody>
                        <ModalFooter display="flex" gap="2" justifyContent="center">
                            <Button colorScheme="red" w="40" onClick={handleDelete}>
                                Delete
                            </Button>
                            <Button variant="outline" colorScheme="red" w="40" onClick={onDeleteClose}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Table Display with Skeleton */}
                {isLoading ? (
                    <SkeletonTable />
                ) : (
                    <TableContainer>
                        <Table size="sm">
                            <Thead className="bg-gray-200">
                                <Tr>
                                    <Th>ID</Th>
                                    <Th>Title</Th>
                                    <Th>Resume Identity</Th>
                                    <Th>Status</Th>
                                    <Th>Created At</Th>
                                    <Th>Action</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {!resumeBuilders || resumeBuilders.length === 0 ? (
                                    <Tr>
                                        <Td colSpan="6" className="text-center py-4">
                                            No data available
                                        </Td>
                                    </Tr>
                                ) : (
                                    resumeBuilders.map((builder, index) => (
                                        <Tr key={builder.uuid}>
                                            <Td>{index + 1}</Td>
                                            <Td>{builder.title}</Td>
                                            <Td>{builder.resume_identity}</Td>
                                            <Td>
                                                <Text color={builder.is_active === 1 ? "green.500" : "red.500"} fontWeight="medium">
                                                    {builder.is_active === 1 ? "Active" : "Inactive"}
                                                </Text>
                                            </Td>
                                            <Td>{formatDate(builder.created_at)}</Td>
                                            <Td>
                                                <HStack spacing={2}>
                                                    <Tooltip label="Edit Resume Builder" placement="top">
                                                        <IconButton
                                                            icon={<EditIcon />}
                                                            colorScheme="blue"
                                                            size="sm"
                                                            onClick={() => handleOpenEdit(builder)}
                                                            aria-label="Edit resume builder"
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label="Delete Resume Builder" placement="top">
                                                        <IconButton
                                                            icon={<DeleteIcon />}
                                                            colorScheme="red"
                                                            size="sm"
                                                            onClick={() => handleOpenDelete(builder)}
                                                            aria-label="Delete resume builder"
                                                        />
                                                    </Tooltip>
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    ))
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        </div>
    )
}

export default AdminResumeBuilder
